import { useEffect } from "react";
import {
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useTranslation } from "@/shared/i18n";
import { useToast } from "@/shared/hooks/use-toast";
import { adminFinancialOverviewApi } from "../api/adminFinancialOverview.api";
import type {
  GetFinancialOverviewSummaryParams,
  FinancialOverviewSummaryResponse,
} from "../types";
import { adminFinancialOverviewKeys } from "./adminFinancialOverview.keys";

type SummaryQueryOptions = Omit<
  UseQueryOptions<FinancialOverviewSummaryResponse, Error>,
  "queryKey" | "queryFn"
>;

export function useFinancialOverviewSummary(
  params?: GetFinancialOverviewSummaryParams,
  options?: SummaryQueryOptions
) {
  const { t } = useTranslation("admin-financial-overview");
  const { toast } = useToast();

  const query = useQuery<FinancialOverviewSummaryResponse, Error>({
    queryKey: adminFinancialOverviewKeys.summary(params),
    queryFn: ({ signal }) =>
      adminFinancialOverviewApi.summary(params, signal),
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: t("errorLoad"),
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, t, toast]);

  return {
    summary: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}
